import React, { useState, useEffect } from "react";
import { SkeletonDashboard } from "../components/SkeletonLoader";
import { useDelayedLoading } from "../hooks/useDelayedLoading";
import FadeIn from "../components/FadeIn";
import { Link } from "react-router-dom";
import { Users, FileText, Calendar, CarFront, Dice3, Dice4, TrendingUp, PieChart } from "lucide-react";
import { MACHINE_STATUS_LABELS, MACHINE_STATUS_COLORS } from '../constants';
import { UpcomingDeliveries } from "../components/DeliveryDeadline";
import UpgradeDashboardCards from "../components/upgrades/UpgradeDashboardCards";
import ShipmentsDashboardCard from "../components/shipments/ShipmentsDashboardCard";

const Dashboard = ({ user }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const showLoading = useDelayedLoading(loading, 250);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const res = await fetch("/api/dashboard/stats", { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setStats(data.data);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  if (showLoading) return <SkeletonDashboard />;

  const tr3Count = stats?.byType?.filter(t => t.type === "TR3" || t.type === "TR3 (1. sk.)")
    .reduce((sum, t) => sum + (parseInt(t.count) || 0), 0);
  const tr4Count = stats?.byType?.filter(t => t.type === "TR4" || t.type === "TR4 (1. sk.)")
    .reduce((sum, t) => sum + (parseInt(t.count) || 0), 0);

  const statCards = [
    { title: "Zákazníci", value: stats?.totalCustomers || 0, icon: Users, color: "#2196F3", link: "/customers" },
    { title: "Trenažéry", value: stats?.totalMachines || 0, icon: CarFront, color: "#4CAF50", link: "/machines" },
    { title: "TR3", value: tr3Count, icon: Dice3, color: "#9C27B0", link: "/machines?type=TR3" },
    { title: "TR4", value: tr4Count, icon: Dice4, color: "#FF9800", link: "/machines?type=TR4" }
  ];

  const statusLabels = MACHINE_STATUS_LABELS;
  const statusColors = Object.fromEntries(
    Object.entries(MACHINE_STATUS_COLORS).map(([k, v]) => [k, v.color])
  );

  return (
    <FadeIn show={!showLoading}>
      <div style={{ padding: "24px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "24px" }}>
        <h1 style={{ fontSize: "24px", fontWeight: 700, color: "#333", margin: 0 }}>Dashboard</h1>
        <p style={{ fontSize: "14px", color: "#999", display: "flex", alignItems: "center", gap: "4px" }}>
          <Calendar size={14} /> {new Date().toLocaleDateString("cs", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
        </p>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: "16px", marginBottom: "24px" }}>
        {statCards.map((card) => (
          <Link key={card.title} to={card.link} style={{ textDecoration: "none" }}>
            <div style={{ backgroundColor: "#FFF", borderRadius: "12px", border: "1px solid #E0E0E0", padding: "20px", boxShadow: "0 2px 4px rgba(0,0,0,0.05)", transition: "transform 0.2s ease, box-shadow 0.2s ease" }}
                 onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.1)"; }}
                 onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.05)"; }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <p style={{ fontSize: "14px", color: "#999", margin: "0 0 4px" }}>{card.title}</p>
                  <p style={{ fontSize: "32px", fontWeight: 700, color: "#333", margin: 0 }}>{card.value}</p>
                </div>
                <div style={{ backgroundColor: card.color, padding: "12px", borderRadius: "12px" }}>
                  <card.icon color="#FFF" size={24} />
                </div>
              </div>
            </div>
          </Link>
        ))}
      </div>

      <UpgradeDashboardCards />

      {user?.role === "admin" && <ShipmentsDashboardCard />}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))", gap: "24px" }}>
        <UpcomingDeliveries items={stats?.upcomingDeliveries || []} />

        {/* Top Customers */}
        <div style={{ backgroundColor: "#FFF", borderRadius: "12px", border: "1px solid #E0E0E0", padding: "20px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: "0 0 16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <TrendingUp size={20} color="#E53935" /> Top zákazníci
          </h2>
          {stats?.topCustomers?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {stats.topCustomers.map((customer, index) => (
                <Link key={customer.name} to={`/customers/${customer.id}`} style={{ textDecoration: "none" }}>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    padding: "8px 12px", 
                    borderRadius: "8px", 
                    backgroundColor: index === 0 ? "#FFF3E0" : "#F5F5F5",
                    transition: "background-color 0.2s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = index === 0 ? "#FFE0B2" : "#EEEEEE"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = index === 0 ? "#FFF3E0" : "#F5F5F5"; }}>
                    <span style={{ fontWeight: 500, color: "#333" }}>{customer.name}</span>
                    <span style={{
                      fontSize: "14px",
                      fontWeight: 600,
                      color: index === 0 ? "#F57C00" : "#666",
                      padding: "2px 8px",
                      borderRadius: "4px",
                      backgroundColor: index === 0 ? "#FFF8E1" : "transparent",
                      whiteSpace: "nowrap",
                      minWidth: "110px",
                      textAlign: "right"
                    }}>
                      {customer.count} {customer.count === 1 ? 'trenažér' : customer.count <= 4 ? 'trenažéry' : 'trenažérů'}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p style={{ color: "#999", textAlign: "center", padding: "16px" }}>Zatím žádní zákazníci</p>
          )}
        </div>

        {/* Machine Status */}
        <div style={{ backgroundColor: "#FFF", borderRadius: "12px", border: "1px solid #E0E0E0", padding: "20px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: "0 0 16px", display: "flex", alignItems: "center", gap: "8px" }}>
            <PieChart size={20} color="#E53935" /> Stavy trenažérů
          </h2>
          {stats?.machinesByStatus?.length > 0 ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {stats.machinesByStatus.map((status) => (
                <Link key={status.status} to={`/machines?status=${status.status}`} style={{ textDecoration: "none" }}>
                  <div style={{ 
                    display: "flex", 
                    justifyContent: "space-between", 
                    alignItems: "center",
                    padding: "8px 12px", 
                    borderRadius: "8px", 
                    backgroundColor: "#F5F5F5",
                    transition: "background-color 0.2s ease",
                    cursor: "pointer"
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#EEEEEE"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#F5F5F5"; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <div style={{ 
                        width: "12px", 
                        height: "12px", 
                        borderRadius: "50%", 
                        backgroundColor: statusColors[status.status] || "#666"
                      }}></div>
                      <span style={{ fontSize: "14px", color: "#333" }}>
                        {statusLabels[status.status] || status.status}
                      </span>
                    </div>
                    <span style={{ fontSize: "14px", fontWeight: 600, color: "#333" }}>{status.count}</span>
                  </div>
                </Link>
              ))}
            </div>
          ) : (
            <p style={{ color: "#999", textAlign: "center", padding: "16px" }}>Zatím žádné trenažéry</p>
          )}
        </div>

        {/* Quick Actions */}
        <div style={{ backgroundColor: "#FFF", borderRadius: "12px", border: "1px solid #E0E0E0", padding: "20px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: 600, color: "#333", margin: "0 0 16px" }}>Rychlé akce</h2>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "12px" }}>
            <Link to="/customers" style={{ padding: "10px 16px", backgroundColor: "#E53935", color: "#FFF", borderRadius: "8px", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 500 }}>
              <Users size={18} /> Zákazníci
            </Link>
            <Link to="/machines" style={{ padding: "10px 16px", backgroundColor: "#F5F5F5", color: "#333", borderRadius: "8px", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 500 }}>
              <CarFront size={18} /> Trenažéry
            </Link>
            <Link to="/templates" style={{ padding: "10px 16px", backgroundColor: "#F5F5F5", color: "#333", borderRadius: "8px", textDecoration: "none", display: "flex", alignItems: "center", gap: "8px", fontSize: "14px", fontWeight: 500 }}>
              <FileText size={18} /> Šablony
            </Link>
          </div>
        </div>
      </div>
    </div>
      </FadeIn>
  );
};

export default Dashboard;
